import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

import TablePagination from '../widgets/pagination'

const PAGE_SIZE = 10

// good/warning/critical thresholds ported from the dashboard mockup's own statusFor(): zero
// in-flight with pending work is the worst case (critical), any other pending backlog is a
// warning, no pending backlog is good -- independent of how close to the ceiling the entity is.
const statusFor = (row) => {
  if (row.in_flight === 0 && row.pending > 0) return { cls: 'critical', label: 'Waiting for a slot' }
  if (row.pending > 0) return { cls: 'warning', label: 'Capped, more waiting' }
  return { cls: 'good', label: 'Full share' }
}

// Entity profile links -- this repo's existing convention (trial_periods_table.js,
// user_list_formatter.js). `user_type` is read by individual_user_info's URL parsing nowhere
// (only user_id is), so it's safe to omit for Data Pulse's coarse user/org entity type.
const linkFor = (client, row) => (
  row.type === 'user'
    ? `/${client}/individual_user_info?user_id=${row.id}`
    : `/${client}/organization_info?org_id=${row.id}`
)

const StatusPill = ({ row }) => {
  const status = statusFor(row)
  return (
    <span className={'dpfu-status-pill ' + status.cls}>
      <span className='dot' />
      {status.label}
    </span>
  )
}

/* Paginated top-N entity breakdown (client-side pagination over the already-fetched, server-sorted
 * top array -- Phase 13a's endpoint bounds this to top_n entries, no re-fetch per page). */
const BreakdownTable = ({ client, rows, totalActive }) => {
  const [page, setPage] = useState(0)

  // Land back on a valid page when a poll shrinks the row count out from under the current page.
  // Keyed on rows.length, not rows itself -- every poll returns a brand-new array even when the
  // contents are unchanged, which would otherwise yank the user back to page 1 every 5 minutes.
  useEffect(() => { setPage(0) }, [rows.length])

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE)
  const truncated = totalActive > rows.length

  return (
    <div className='dpfu-table-card'>
      <div className='dpfu-table-scroll'>
        <table className='dpfu-table'>
          <thead>
            <tr>
              <th>Entity</th>
              <th>In flight</th>
              <th>Allocated <span className='dpfu-th-sub'>(max)</span></th>
              <th>Pending</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className='dpfu-empty'
                >
                  No active users or organizations right now.
                </td>
              </tr>
            ) : pageRows.map((row) => (
              <tr key={row.type + ':' + row.id}>
                <td>
                  <Link
                    className='dpfu-entity-link'
                    to={linkFor(client, row)}
                    title={'Open ' + row.id + '’s profile'}
                  >
                    {row.id}
                  </Link>
                  <span className='dpfu-entity-type'>{row.type}</span>
                </td>
                <td className='dpfu-num'>{row.in_flight}</td>
                <td className='dpfu-num'>
                  {row.in_flight} <span className='dpfu-alloc-max'>({row.max})</span>
                </td>
                <td className='dpfu-num'>{row.pending}</td>
                <td><StatusPill row={row} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className='dpfu-table-foot'>
        <span className='dpfu-result-count'>
          {rows.length === 0
            ? 'No results'
            : 'Showing ' + (safePage * PAGE_SIZE + 1) + '–' + Math.min(rows.length, safePage * PAGE_SIZE + PAGE_SIZE) +
              ' of ' + rows.length + (truncated ? ' (top ' + rows.length + ' by demand, of ' + totalActive + ' active)' : '')}
        </span>
        {totalPages > 1 && (
          <TablePagination
            currentPageIndex={safePage}
            totalPage={totalPages}
            updateTableData={setPage}
          />
        )}
      </div>
    </div>
  )
}

export default BreakdownTable
